import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './Feeding.module.scss';
import Button from 'components/UI/V3/Button/Button';
import useNonInitialEffect from '@versiondos/hooks';
import { MarketplaceStatus } from '@spike/marketplace-action';
import { RootState } from 'store';
import { useSettingsMarketplace, useMasterData } from 'hooks';
import BusinessMoment from './BusinessMoment';
import Marketplace, { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import MasterData from '@spike/masterdata-model';
import { getFeedingSlotTimes } from 'utils/feedingSlotTimes';

export interface FeedingProps {
    className?: string;
    onSave?: (feedingMoments: FeedingMoments) => void;
}

export interface FeedingMoment {
    id: number;
    name: string;
    time: string;
    selected: boolean;
}

export interface FeedingMoments {
    morning: FeedingMoment;
    afternoon: FeedingMoment;
    evening: FeedingMoment;
}

type FeedingErrors = {
    morning?: string;
    afternoon?: string;
    evening?: string;
};

const DEFAULT_FEEDING_TIMES: Record<keyof FeedingMoments, string> = {
    morning: '08:00',
    afternoon: '13:00',
    evening: '17:00'
};

const toMinutes = (time: string): number => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

export const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    if (timeString.length === 1) return `0${timeString}:00`;
    if (timeString.length === 2) return `${timeString}:00`;
    if (timeString.length === 3) return `0${timeString.slice(0, 1)}:${timeString.slice(1)}`;
    if (timeString.length === 4) return `${timeString.slice(0, 2)}:${timeString.slice(2)}`;
    return timeString;
};

const validateTimes = (moments: FeedingMoments): FeedingErrors => {
    const errors: FeedingErrors = {};

    const morningMinutes = toMinutes(moments.morning.time);
    const afternoonMinutes = toMinutes(moments.afternoon.time);
    const eveningMinutes = toMinutes(moments.evening.time);

    if (morningMinutes >= afternoonMinutes) {
        errors.morning = 'Morning time must be earlier than Afternoon time.';
    }

    if (afternoonMinutes >= eveningMinutes) {
        errors.afternoon = 'Afternoon time must be later than Morning time and earlier than Evening time';
    }

    if (
        morningMinutes === afternoonMinutes ||
        morningMinutes === eveningMinutes ||
        afternoonMinutes === eveningMinutes
    ) {
        errors.evening = 'Evening time must be later than Afternoon time.';
    }

    return errors;
};

const getFeedingMoments = (marketplace: Marketplace, masterData: MasterData): FeedingMoments => {
    const times = getFeedingSlotTimes(marketplace, masterData, { fallbackToDefaults: true });
    return {
        morning: {
            id: 1,
            name: 'Morning',
            time: times.morning,
            selected:
                marketplace.settings.find(s => s.key === 'morning_feeding_enabled')?.value === SETTINGS_TRUE_VALUE ||
                false
        },
        afternoon: {
            id: 2,
            name: 'Afternoon',
            time: times.afternoon,
            selected:
                marketplace.settings.find(s => s.key === 'afternoon_feeding_enabled')?.value === SETTINGS_TRUE_VALUE ||
                false
        },
        evening: {
            id: 3,
            name: 'Evening',
            time: times.evening,
            selected:
                marketplace.settings.find(s => s.key === 'evening_feeding_enabled')?.value === SETTINGS_TRUE_VALUE ||
                false
        }
    };
};

export const Feeding: FunctionComponent<FeedingProps> = props => {
    const marketplace = useSettingsMarketplace();
    const masterData = useMasterData();
    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [feeding, setFeeding] = useState<FeedingMoments>(getFeedingMoments(marketplace, masterData));
    const [errors, setErrors] = useState<FeedingErrors>({});
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveSuccess || status === MarketplaceStatus.SaveSettingsMarketplaceSuccess) {
            setSaved(true);
            setLoading(false);
        }
    }, [status]);

    const handleSave = () => {
        if (Object.keys(errors).length > 0) return;
        props.onSave?.({ ...feeding });
        setLoading(true);
    };

    const updateMomentTime = (key: keyof FeedingMoments, time: string) => {
        setFeeding(prev => {
            const next = {
                ...prev,
                [key]: { ...prev[key], time }
            };
            setErrors(validateTimes(next));
            return next;
        });
    };

    const toggleMoment = (key: keyof FeedingMoments) => {
        setFeeding(prev => {
            const isDisabling = prev[key].selected;

            const next = {
                ...prev,
                [key]: {
                    ...prev[key],
                    selected: !prev[key].selected,
                    time: isDisabling ? DEFAULT_FEEDING_TIMES[key] : prev[key].time
                }
            };

            setErrors(validateTimes(next));
            return next;
        });
    };

    const isInvalid = Object.keys(errors).length > 0;

    return (
        <div className={styles.container}>
            <DefaultHeader
                title="Task Schedule"
                textBelowTitle="Set default time slots for care tasks such as feeding, relief, hydration, and other daily activities."
            />

            <div className={styles.businessMomentContainer}>
                <BusinessMoment
                    label={feeding.morning.name}
                    feedingMoment={feeding.morning.time}
                    selected={feeding.morning.selected}
                    errorMessage={errors.morning}
                    onChange={time => updateMomentTime('morning', time)}
                    onSelected={() => toggleMoment('morning')}
                    className={styles.businessMoment}
                />

                <BusinessMoment
                    label={feeding.afternoon.name}
                    feedingMoment={feeding.afternoon.time}
                    selected={feeding.afternoon.selected}
                    errorMessage={errors.afternoon}
                    onChange={time => updateMomentTime('afternoon', time)}
                    onSelected={() => toggleMoment('afternoon')}
                    className={styles.businessMoment}
                />

                <BusinessMoment
                    label={feeding.evening.name}
                    feedingMoment={feeding.evening.time}
                    selected={feeding.evening.selected}
                    errorMessage={errors.evening}
                    onChange={time => updateMomentTime('evening', time)}
                    onSelected={() => toggleMoment('evening')}
                    className={styles.businessMoment}
                />
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    label={saved ? 'Saved!' : 'Save'}
                    onClick={handleSave}
                    isLoading={loading}
                    disabled={isInvalid}
                />
            </div>
        </div>
    );
};

export default Feeding;
